import React, { FC, ReactElement, ReactNode, useEffect, useMemo } from 'react'
import { RouterConfigEntry } from '../../../Types/Types'
import { useRouterConfig } from '../../../Hooks/useRouterConfig'
import { HeaderTop } from './Components/HeaderTop'
import { HeaderSidebar } from './Components/HeaderSidebar'
import { InviteToast } from './Components/InviteToast'
import { RouteID } from './Types/Types'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { useUserSettings } from '../../../../React/Common/Hooks/useUserSettings'
import {
    DISPLAY_STYLE_NONE,
    IS_FUSION_LEGACY_INITIAL_URL,
    IS_FUSION_PORTAL_INITIAL_URL,
    IS_IN_IFRAME
} from './Constants'
import { confirmFusionLegacyMode } from '../../../../React/Common/API/UserSignInOutAPI'

type Props = {
    routeID: Definable<RouteID>
    children: ReactNode
}

export const Header: FC<Props> = ({ routeID, children }: Props): ReactElement => {
    const routerConfig: Nullable<RouterConfigEntry> = useRouterConfig()
    const [userSettings] = useUserSettings()
    const [userInfo]: UserInfo = useUserInfo()

    const disableHeader: boolean =
        IS_FUSION_PORTAL_INITIAL_URL ||
        (IS_IN_IFRAME && !!userSettings?.FusionPortalMode) ||
        !!routerConfig?.disableHeader

    const fusionLegacyMode: boolean = useMemo(
        (): boolean => IS_FUSION_LEGACY_INITIAL_URL || (IS_IN_IFRAME && !!userSettings?.FusionLegacyMode),
        [userSettings]
    )

    // The new-UI invite only applies to authenticated users: on public routes (login,
    // forgot/reset password, policies) the "Check it out" SSO exchange has no session
    // and errors (APD-12932). It is also gated by the CME's Classic/CX Suite toggle
    // capability so it is suppressed alongside the header toggle (APD-13255).
    const showInviteToast: boolean = routerConfig?.requiresAuthentication === true && !!userInfo?.ShowUiVersionToggle

    useEffect((): void => {
        confirmFusionLegacyMode(fusionLegacyMode)
    }, [fusionLegacyMode])

    if (disableHeader) {
        return (
            <section className='router-main-content-section'>
                {children}
                {showInviteToast && <InviteToast />}
            </section>
        )
    }

    return (
        <div className={fusionLegacyMode ? 'main-nav-container fusion-legacy-mode' : 'main-nav-container'}>
            <header className='router-header' style={fusionLegacyMode ? { display: DISPLAY_STYLE_NONE } : undefined}>
                <HeaderTop routeID={routeID} />
            </header>
            <section className='router-main-content-section'>
                <aside className='router-sidebar'>
                    <HeaderSidebar routeID={routeID} fusionLegacyMode={fusionLegacyMode} />
                </aside>
                {children}
            </section>
            {showInviteToast && <InviteToast />}
        </div>
    )
}
